import { Minimatch } from "minimatch";

export interface IGlob {
	path: string;

	isMatch(path: string): boolean;
	isCongruent(path: string | IGlob): boolean;
}

class Segment {
	private static readonly SegmentType = {
		Globstar: "Globstar",
		Glob: "Glob",
		Pattern: "Pattern",
		Static: "Static"
	} as const;

	private previous: Segment;
	private next: Segment;
	private matcher: Minimatch;

	private value: string;
	private type: string;

	private isGlobstar = false;
	private isWild = false;
	private isPattern = false;
	private isStatic = false;

	private isHead = false;
	private head: Segment;

	private get isTail(): boolean {
		return this.next === undefined;
	}

	private get tail(): Segment {
		return this.isTail ? this : this.next?.tail;
	}

	private constructor(previous: Segment, value: string) {
		this.previous = previous;
		this.value = value;
		this.value = value.length === 0 ? "/" : value;

		if(this.value === "**") {
			this.isGlobstar = (this.type = Segment.SegmentType.Globstar) === Segment.SegmentType.Globstar;
		}
		else if(this.value === "*") {
			this.isWild = (this.type = Segment.SegmentType.Glob) === Segment.SegmentType.Glob;
		}
		else if(this.value === "/" || /^[a-zA-Z]+$/.test(this.value)) {
			this.isStatic = (this.type = Segment.SegmentType.Static) === Segment.SegmentType.Static;
		}
		else {
			this.isPattern = (this.type = Segment.SegmentType.Pattern) === Segment.SegmentType.Pattern;
		}

		this.isHead = this.previous === undefined;
		this.head = this.isHead ? this : this.previous?.head;

		if(this.isPattern) {
			this.matcher = new Minimatch(this.value);
		}
	}

	public static parse(path: string): Segment {
		if(!path) {
			throw new Error("ArgumentNullException: path");
		}

		const segments = path.split("/");

		const head = new Segment(undefined, segments[0]);
		if(segments.length > 1) {
			let tail = head;
			for(let i = 1; i < segments.length; i++) {
				tail = tail.append(segments[i]);
			}
		}

		return head;
	}

	private static isCongruent(left: Segment, right: Segment): boolean {
		if(left.isGlobstar) {
			return Segment.isGlobCongruentTo(left, right);
		}
		if(right.isGlobstar) {
			return Segment.isGlobCongruentTo(right, left);
		}

		if(left.isWild) {
			return Segment.isWildCongruentTo(left, right);
		}
		if(right.isWild) {
			return Segment.isWildCongruentTo(right, left);
		}

		if(left.isPattern) {
			return Segment.isPatternCongruentTo(left, right);
		}
		if(right.isPattern) {
			return Segment.isPatternCongruentTo(right, left);
		}

		return Segment.isStaticCongruentTo(left, right);
	}

	private static isGlobCongruentTo(left: Segment, right: Segment): boolean {
		if(left.isTail) {
			// this globstar will encompass EVERYTHING
			return true;
		}

		let other = right;

		// start at depth 0 of the segment (other)
		// globstars are 0 or more consumed directories
		// iterate deeper and check again until match
		do {
			if(Segment.isCongruent(left.next, other)) {
				return true;
			}
			if(other.isTail) {
				break;
			}

			other = other.next;

			if(other.isTail && Segment.isCongruent(left.next, other)) {
				return true;
			}
		}
		while(!other.isTail);

		return false;
	}

	private static isWildCongruentTo(left: Segment, right: Segment): boolean {
		return Segment.isNextCongruent(left, right);
	}

	private static isPatternCongruentTo(left: Segment, right: Segment): boolean {
		if(!left.matcher?.match(right.value, false)) {
			return false;
		}

		return Segment.isNextCongruent(left, right);
	}

	private static isStaticCongruentTo(left: Segment, right: Segment): boolean {
		if(!(left.value === right.value)) {
			return false;
		}

		return Segment.isNextCongruent(left, right);
	}

	private static isNextCongruent(left: Segment, right: Segment): boolean {
		if(left.isTail && right.isTail) {
			return true;
		}
		if(left.isTail) {
			return Segment.endsInGlobStar(right.next);
		}
		if(right.isTail) {
			return Segment.endsInGlobStar(left.next);
		}

		return Segment.isCongruent(left.next, right.next);
	}

	private static endsInGlobStar(segment: Segment | undefined): boolean {
		while(segment !== undefined && segment.isGlobstar) {
			if(segment.isTail) {
				return true;
			}
			segment = segment.next;
		}

		return false;
	}

	public isCongruentTo(segment: Segment): boolean {
		return Segment.isCongruent(this, segment);
	}

	private append(segment: string): Segment {
		if(this.next !== undefined) {
			throw new Error("InvalidOperationException");
		}

		return (this.next = new Segment(this, segment));
	}
}

export class Glob implements IGlob {
	path: string;
	matcher: Minimatch;
	private head: Segment;

	constructor(path: string) {
		this.path = path;
		this.matcher = new Minimatch(path);
		this.head = Segment.parse(this.path);
	}

	public isMatch(path: string) {
		return this.matcher.match(path, false);
	}

	public isCongruent(path: string | IGlob): boolean {
		if((path as Glob)?.head) {
			return this.head.isCongruentTo((path as Glob).head);
		}
		if((path as IGlob)?.path) {
			return this.isCongruent((path as IGlob).path);
		}

		return this.isCongruent(new Glob(path as string));
	}
}
